import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../store';
import { AttentionService } from '../../api/AttentionAPI';
import { ShiftsService } from '../../api/ShiftsAPI';
import { ScheduleService } from '../../api/ScheduleAPI';
import { formatDisplayDate, toDateStr } from '../../utils';
import { canViewPage } from '../../constants';
import logoLight from '../../assets/images/icon-only-black.png';
import logoDark from '../../assets/images/icon-only-bronze.png';
import s from '../../styles/dashboard.module.css';

// Sun and moon for the theme switch: the icon names the theme you would get.
function MoonIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  );
}
function SunIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </svg>
  );
}

const NAV = [
  { key: 'queue', label: 'Queue', to: '/dashboard' },
  { key: 'shifts', label: 'My Shifts', to: '/shifts' },
  { key: 'activity', label: 'Activity', items: [
    { key: 'appointments', label: 'Appointments', to: '/appointments' },
    { key: 'payments',     label: 'Payments',     to: '/transaction' },
  ] },
  { key: 'reports', label: 'Reports', items: [
    { key: 'tips',        label: 'Tips',        to: '/reports/tips' },
    { key: 'receivables', label: 'Receivables', to: '/reports/receivables' },
    { key: 'analytics',   label: 'Analytics',   to: '/reports/analytics' },
  ] },
  { key: 'access', label: 'Access', items: [
    { key: 'users',    label: 'Users',    to: '/admin' },
    { key: 'staff',    label: 'Staff',    to: '/staff' },
    { key: 'schedule',   label: 'Schedule',   to: '/schedule' },
    { key: 'timesheets', label: 'Timesheets', to: '/timesheets' },
  ] },
  { key: 'attention', label: 'Need attention', to: '/attention', pill: true },
];

// The nav as the sheet shows it: the loose top-level entries gathered under
// one heading, then each dropdown as its own. Built from the same list the bar
// uses, so it inherits the role filtering and cannot drift from it.
function sheetGroups(items) {
  const loose = items.filter(n => !n.items);
  const groups = items.filter(n => n.items);
  return (loose.length ? [{ key: 'today', label: 'Today', items: loose }] : [])
    .concat(groups);
}

// What this role may actually open. The keys above are the permission keys, so
// this asks the same question the router does rather than keeping a second
// list that can drift from it. A group left with no items drops out too: an
// empty dropdown is worse than no dropdown.
function visibleNav(user) {
  return NAV.reduce((acc, n) => {
    if (!n.items) {
      if (canViewPage(user, n.key)) acc.push(n);
      return acc;
    }
    const items = n.items.filter(i => canViewPage(user, i.key));
    if (items.length) acc.push({ ...n, items });
    return acc;
  }, []);
}

// "8:00 AM" on the wall, for the shift line under the floating clock.
const fmtClock = (v) => (v
  ? new Date(v).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
  : '');

const dur = (mins) => {
  const n = Math.max(0, Math.round(mins || 0));
  if (!n) return '0m';
  const hrs = Math.floor(n / 60);
  return hrs ? `${hrs}h${n % 60 ? ` ${n % 60}m` : ''}` : `${n}m`;
};

function activeInfo(location) {
  const { pathname, search } = location;
  if (pathname.startsWith('/reports/tips'))        return { group: 'reports', item: 'tips' };
  if (pathname.startsWith('/reports/receivables')) return { group: 'reports', item: 'receivables' };
  if (pathname.startsWith('/reports/analytics'))   return { group: 'reports', item: 'analytics' };
  if (pathname.startsWith('/attention'))    return { group: 'attention', item: null };
  if (pathname.startsWith('/shifts'))       return { group: 'shifts', item: null };
  if (pathname.startsWith('/schedule'))     return { group: 'access', item: 'schedule' };
  if (pathname.startsWith('/timesheets'))   return { group: 'access', item: 'timesheets' };
  if (pathname.startsWith('/appointments')) return { group: 'activity', item: 'appointments' };
  if (pathname.startsWith('/transaction'))  return { group: 'activity', item: 'payments' };
  if (pathname.startsWith('/admin'))        return { group: 'access', item: 'users' };
  if (pathname.startsWith('/staff'))        return { group: 'access', item: 'staff' };
  return { group: 'queue', item: 'queue' };
}

// `wide` drops the 1600px column the bar normally keeps its contents in, for
// a page that runs the full window — the queue board.
export function Header({ lightMode, toggleLightMode, lastFetch, isToday = true, selectedDate, taxMode, onTaxModeChange, wide = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [openKey, setOpenKey] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const navRef = useRef(null);

  const { group: activeGroup, item: activeItem } = activeInfo(location);

  const navItems = visibleNav(user);

  const { data: attention } = useQuery({
    queryKey: ['attention', toDateStr(new Date()), toDateStr(new Date())],
    queryFn: () => AttentionService.list(toDateStr(new Date()), toDateStr(new Date())),
    // The pill is a nav item like any other, so it fetches exactly when it is
    // shown rather than on a separate rule that can fall out of step with it.
    enabled: canViewPage(user, 'attention'),
    staleTime: 60000,
    refetchInterval: 120000,
  });
  const attentionCount = attention?.total || 0;

  // The clock lives in the header so it is reachable from whatever page
  // somebody is on when their shift starts. Only for a login that is actually
  // somebody on the roster — the same rule that decides whether they get a
  // shifts page at all, since an office account has no timecard to punch.
  const canClock = canViewPage(user, 'shifts') && !!user?.idStaff;
  const queryClient = useQueryClient();
  const [clocking, setClocking] = useState(false);

  const { data: openData } = useQuery({
    queryKey: ['shift-open', user?.idStaff],
    queryFn: () => ShiftsService.openFor(user.idStaff),
    enabled: canClock,
    refetchInterval: 60000,
  });
  const openShift = openData?.shift || null;

  // Today's planned shift, for the line under the floating clock. One day's
  // worth, so it costs little to ask for on every page.
  const todayKey = toDateStr(new Date());
  const { data: todayPlan } = useQuery({
    queryKey: ['shift-today', user?.idStaff, todayKey],
    queryFn: () => ScheduleService.mine(todayKey, todayKey),
    enabled: canClock,
    staleTime: 300000,
  });
  const todayShift = (todayPlan?.scheduled || [])[0] || null;

  const toggleClock = async () => {
    setClocking(true);
    try {
      if (openShift) await ShiftsService.clockOut({ idShift: openShift.idShift });
      else await ShiftsService.clockIn(user.idStaff, null, null);
    } catch {
      // The shifts page is where a failure can actually be explained; here the
      // refetch below just puts the button back to the truth.
    } finally {
      setClocking(false);
      queryClient.invalidateQueries({ queryKey: ['shift-open'] });
      queryClient.invalidateQueries({ queryKey: ['my-shifts'] });
    }
  };

  useEffect(() => { setOpenKey(null); setSheetOpen(false); }, [location.pathname, location.search]);

  useEffect(() => {
    if (!openKey) return;
    const onDoc = (e) => { if (navRef.current && !navRef.current.contains(e.target)) setOpenKey(null); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [openKey]);

  const handleLogout = () => { logout(); navigate('/login'); };

  const syncText = lastFetch
    ? (isToday ? `SYNCED ${lastFetch}` : `VIEWED ${formatDisplayDate(selectedDate)}`)
    : null;

  return (
    <header className={`${s.header} ${lightMode ? s.headerLight : ''} ${wide ? s.headerWide : ''}`}
            style={{ flexShrink: 0 }}>
      <button type="button" className={s.burger} aria-label="Menu"
              onClick={() => setSheetOpen(true)}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
             strokeWidth="1.9" strokeLinecap="square"><path d="M4 7h16M4 12h16M4 17h16" /></svg>
      </button>
      <span className={s.logo}>
        <img className={s.logoImg} src={logoDark} alt="ACE CAR SPA" />
      </span>

      <nav className={s.nav} ref={navRef}>
        {navItems.map((n) => {
          if (n.pill) {
            return (
              <Link
                key={n.key}
                to={n.to}
                className={`${s.navPill} ${attentionCount ? s.navPillAlert : s.navPillClear} ${
                  activeGroup === n.key ? s.navPillActive : ''}`}
              >
                {attentionCount > 0 && <span className={s.navPillDot} aria-hidden="true" />}
                {attentionCount > 0 ? `${attentionCount} ${n.label}` : 'All clear'}
              </Link>
            );
          }
          if (!n.items) {
            return (
              <Link key={n.key} to={n.to} className={`${s.navBtn} ${activeGroup === n.key ? s.active : ''}`}>
                {n.label}
              </Link>
            );
          }
          const open = openKey === n.key;
          return (
            <div key={n.key} className={s.navGroup}>
              <button
                type="button"
                className={`${s.navBtn} ${activeGroup === n.key ? s.active : ''}`}
                onClick={() => setOpenKey(open ? null : n.key)}
                aria-expanded={open}
              >
                {n.label}
                <span className={s.navCaret}>▾</span>
              </button>
              {open && (
                <div className={`${s.navMenu} ${lightMode ? s.navMenuLight : ''}`}>
                  {n.items.map((it) => (
                    it.to ? (
                      <Link
                        key={it.key}
                        to={it.to}
                        className={`${s.navMenuItem} ${activeItem === it.key ? s.navMenuItemActive : ''}`}
                        onClick={() => setOpenKey(null)}
                      >
                        {it.label}
                      </Link>
                    ) : (
                      <span key={it.key} className={`${s.navMenuItem} ${s.navMenuItemDisabled}`}>
                        {it.label}
                      </span>
                    )
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      <div className={s.headerRight}>
        {canClock && (
          <button
            type="button"
            onClick={toggleClock}
            disabled={clocking}
            title={openShift ? 'Tap to clock out' : 'Tap to clock in'}
            className={`${s.hdrClock} ${openShift ? s.hdrClockOn : ''}`}
          >
            <span className={s.hdrClockDot} />
            <span>
              {openShift
                ? `On shift \u00b7 ${dur(openShift.minutes)}`
                : clocking ? 'Working\u2026' : 'Clock in'}
            </span>
          </button>
        )}

        <button
          onClick={() => toggleLightMode(l => !l)}
          className={s.themeIconBtn}
          title={lightMode ? 'Switch to dark' : 'Switch to light'}
          aria-label={lightMode ? 'Switch to dark mode' : 'Switch to light mode'}
        >
          {lightMode ? <MoonIcon /> : <SunIcon />}
        </button>

        {taxMode && (
          <div className={s.taxToggle} role="group" aria-label="Show prices">
            <button
              type="button"
              className={`${s.taxOpt} ${taxMode === 'before' ? s.taxOptActive : ''}`}
              aria-pressed={taxMode === 'before'}
              onClick={() => onTaxModeChange?.('before')}
            >
              Before tax
            </button>
            <button
              type="button"
              className={`${s.taxOpt} ${taxMode === 'after' ? s.taxOptActive : ''}`}
              aria-pressed={taxMode === 'after'}
              onClick={() => onTaxModeChange?.('after')}
            >
              After tax
            </button>
          </div>
        )}

        <div className={s.headerClockGroup}>
          <span className={s.headerLiveLabel}>LIVE FROM SQUARE</span>
          {syncText && <span className={s.headerSyncLabel}>{syncText}</span>}
        </div>
        <button onClick={handleLogout} className={s.themeSwitchBtn}>Logout</button>
      </div>
      {/* The clock, floating, for the widths where the bar has had to drop it.
          Not on the shifts page — that page's own card is the clock — and not
          behind the open nav sheet. */}
      {canClock && !sheetOpen && !location.pathname.startsWith('/shifts') && (
        <button type="button"
                className={`${s.clockFab} ${openShift ? s.clockFabOn : ''}`}
                onClick={toggleClock} disabled={clocking}>
          <span className={s.clockFabDot} />
          <span className={s.clockFabText}>
            <span className={s.clockFabLabel}>{openShift ? 'Clock out' : 'Clock in'}</span>
            <span className={s.clockFabSub}>
              {openShift
                ? `On shift \u00b7 ${dur(openShift.minutes)}`
                : todayShift
                  ? `Shift ${fmtClock(todayShift.startAt)} \u2013 ${fmtClock(todayShift.endAt)}`
                  : 'No shift today'}
            </span>
          </span>
        </button>
      )}

      {sheetOpen && createPortal(
        <div className={`${s.navSheet} ${lightMode ? s.headerLight : ''}`}>
          <div className={s.navSheetHead}>
            <img className={s.logoImg} src={logoDark} alt="ACE CAR SPA" />
            <button type="button" className={s.navSheetClose} aria-label="Close"
                    onClick={() => setSheetOpen(false)}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                   strokeWidth="1.9" strokeLinecap="square"><path d="M5 5l14 14M19 5L5 19" /></svg>
            </button>
          </div>

          <div className={s.navSheetBody}>
            {sheetGroups(navItems).map((g) => (
              <div key={g.key}>
                <div className={s.navSheetGroup}>{g.label}</div>
                <div className={s.navSheetItems}>
                  {g.items.map((it) => {
                    const on = g.key === 'today'
                      ? activeGroup === it.key
                      : activeGroup === g.key && activeItem === it.key;
                    return (
                      <Link key={it.key} to={it.to}
                            className={`${s.navSheetItem} ${on ? s.navSheetItemOn : ''}`}>
                        <span>{it.key === 'attention' ? 'Needs attention' : it.label}</span>
                        {it.key === 'attention' && attentionCount > 0 && (
                          <span className={s.navSheetBadge}>{attentionCount}</span>
                        )}
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}

            <div className={s.navSheetFoot}>
              <div className={s.headerClockGroup} style={{ marginLeft: 0, alignItems: 'flex-start' }}>
                <span className={s.headerLiveLabel}>LIVE FROM SQUARE</span>
                {syncText && <span className={s.headerSyncLabel}>{syncText}</span>}
              </div>
              <button onClick={handleLogout} className={s.themeSwitchBtn}
                      style={{ marginLeft: 'auto' }}>Logout</button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </header>
  );
}
